/**
 * Every vue-query key in the app, built here and nowhere else.
 *
 * The shape is `CACHE_AUDIT.md` §5.1, implemented as written. Two rules from
 * `PLAN_TRACK_C.md` C1.1 hold for every entry:
 *
 *  1. No page constructs a key from an inline literal. `grep -rn "queryKey: \["
 *     src/pages src/components` must return nothing.
 *  2. A key carries the **full scope the endpoint varies by** — `user`, `role`,
 *     `company`, `branch`, `page`, `q` — and nothing more (`R-02`, §5.2).
 *
 * The `*Root` prefixes are the coarse, domain-level handles the realtime map
 * invalidates by `predicate` (C1.4b/C1.4d). They are declared once, here, so
 * `realtimeMap.ts` holds no inline literals either.
 *
 * Scoping note (§5.2 vs `PLAN_TRACK_C.md` C2.2/C2.3): the plan's prose says
 * `catalog.list_item_groups`, `customers_mgmt.territories` and
 * `customers_mgmt.list_customer_groups` must include the company. The backend
 * at `~/frappe-bench/apps/glamora` says otherwise — an `Item Group`, a
 * `Territory` and a `Customer Group` carry **no** `company` column and are
 * platform-wide (`glamora/api/catalog.py:645-672`,
 * `glamora/api/customers_mgmt.py:286-321`: "there is nothing tenant-specific to
 * filter on — only the caller's right to read"). The §5.1 sketch and those
 * server comments agree, so no company goes in those keys. See the PR body.
 */

/** A query's filter/scope object; kept loose so each factory stays readable. */
export type Args = Record<string, unknown>

// ── Domain roots: the prefixes a realtime event invalidates ──────────────────

export const appointmentsRoot = ['appointments'] as const
export const frontDeskRoot = ['frontDesk'] as const
export const customersRoot = ['customers'] as const
export const catalogRoot = ['catalog'] as const
export const staffRoot = ['staff'] as const
export const posRoot = ['pos'] as const
export const financeRoot = ['finance'] as const
export const receiptRoot = ['receipt'] as const
export const inventoryRoot = ['inventory'] as const
export const storesRoot = ['stores'] as const
export const companiesRoot = ['companies'] as const
export const branchesRoot = ['branches'] as const

/**
 * An L1 lookup family's root, e.g. `lookupRoot('itemGroups')`.
 *
 * `lookups.itemGroups('service')` is `['lookup', 'itemGroups', 'service']`, so
 * this prefix invalidates the whole family. A lookup that *is* scoped by
 * company (none are today) must add the company to its own factory instead.
 */
export function lookupRoot(name: string): readonly unknown[] {
  return ['lookup', name]
}

// ── The factories ───────────────────────────────────────────────────────────

export const qk = {
  // Roots, so the realtime map references the factory and never a literal.
  appointmentsRoot,
  frontDeskRoot,
  customersRoot,
  catalogRoot,
  staffRoot,
  posRoot,
  financeRoot,
  receiptRoot,
  inventoryRoot,
  storesRoot,
  companiesRoot,
  /** Guest directory page: every filter + page is in the key (C3.1). */
  marketDirectory: (args: Args) => ['market', 'directory', args] as const,
  /** Guest salon page: its services list (`slug` + branch + category + page). */
  marketSalonServices: (args: Args) =>
    ['market', 'salonServices', args] as const,
  /** Guest shop: the paged pickup catalogue (`slug` + branch + page). */
  marketShopCatalog: (args: Args) => ['market', 'shopCatalog', args] as const,
  branchesRoot,
  lookupRoot,

  /** `A` — auth/session. Never cached for real; kept so keys are not literals. */
  boot: (userId: string) => ['boot', userId] as const,

  /** `L2` — the tenant companies of the signed-in user. */
  companies: (userId: string) => ['companies', { userId }] as const,
  /**
   * `L2` — the branches of one company. Role-scoped ("manager sees own
   * branches"), so the user is in the key: a key without it is a leak.
   */
  branches: (company: string, userId: string) =>
    ['branches', company, { userId }] as const,

  /** `L1` — static lookups. Scope only where the endpoint varies by it. */
  lookups: {
    /**
     * Leaf item groups. `type` is the endpoint's own filter; `'all'` is the
     * call that omits it (the commission screens list every leaf group). The
     * endpoint carries no company (`catalog.py:645-672`), so neither does the
     * key.
     */
    itemGroups: (type: 'service' | 'product' | 'all') =>
      ['lookup', 'itemGroups', type] as const,
    genders: () => ['lookup', 'genders'] as const,
    designations: () => ['lookup', 'designations'] as const,
    customerGroups: () => ['lookup', 'customerGroups'] as const,
    territories: () => ['lookup', 'territories'] as const,
    leaveTypes: () => ['lookup', 'leaveTypes'] as const,
  },

  /** `L2` — a catalog list: `{branch, company, group, query, page}`. */
  catalog: (kind: 'services' | 'products', q: Args) =>
    ['catalog', kind, q] as const,
  /** `L2` — the customer directory: `{branch, query, page}`. */
  customers: (q: Args) => ['customers', 'list', q] as const,
  customer: (name: string) => ['customers', 'doc', name] as const,

  /** `L3` — appointments: `{branch, from, to, staff, status, page}`. */
  appointments: (q: Args) => ['appointments', 'list', q] as const,
  appointment: (name: string) => ['appointments', 'doc', name] as const,
  /** `L3` — the front-desk queue: `{branch, date}`. */
  frontDesk: (q: Args) => ['frontDesk', q] as const,

  /** `L3` — stock. A stale quantity is a stock error; never serve into a write. */
  inventory: {
    levels: (q: Args) => ['inventory', 'levels', q] as const,
    movements: (q: Args) => ['inventory', 'movements', q] as const,
    summary: (q: Args) => ['inventory', 'summary', q] as const,
  },

  /** `T1` — money: `{company, from, to, status, query, page}`. */
  finance: (kind: string, q: Args) => ['finance', kind, q] as const,
  /** `T1` — one receipt/report document by name. */
  receipt: (name: string) => ['receipt', name] as const,

  /** `L2` — the supplier directory: `{company, query, include_disabled, page}`. */
  suppliers: (q: Args) => ['suppliers', 'list', q] as const,

  /** `L2` — the staff directory: `{branch, company, query, page}`. */
  staff: (q: Args) => ['staff', 'list', q] as const,
  staffMember: (name: string) => ['staff', 'doc', name] as const,

  /** `L2` — leave requests: `{status, page, page_size}`. */
  leaveRequests: (q: Args) => ['leave', 'list', q] as const,

  /** POS pickers. Server-searched; the bulk lists are not cached (`C-05`). */
  pos: {
    items: (q: Args) => ['pos', 'items', q] as const,
    customers: (q: Args) => ['pos', 'customers', q] as const,
  },

  /** `L3`/`P` — the customer portal. */
  portal: (kind: string, q: Args) => ['portal', kind, q] as const,
  /** `G` — public marketplace reads. */
  marketplace: (kind: string, q: Args) => ['marketplace', kind, q] as const,
} as const
